import { getActiveHardCap } from './stage1-video-policy';
import { getLocalVideoWorkerStatus } from './local-video-worker-adapter';

interface VideoLike {
    size?: unknown;
    sourceMissing?: unknown;
    quoteMakerSourcePath?: unknown;
    relativePath?: unknown;
    webkitRelativePath?: unknown;
    localSourcePath?: unknown;
    syncFileName?: unknown;
    name?: unknown;
    originalName?: unknown;
    processingMode?: unknown;
    storagePolicy?: {
        originalStorage?: unknown;
        canonicalSource?: unknown;
        processingMode?: unknown;
        persistOriginalInIndexedDb?: unknown;
        durabilityStatus?: unknown;
        sourcePath?: unknown;
        version?: unknown;
        uploadOriginalToCloud?: unknown;
    } | null;
    localSourceOnly?: unknown;
    canonicalSource?: unknown;
    s3Key?: unknown;
    cloudPlayback?: {
        kind?: unknown;
        s3Key?: unknown;
    } | null;
    workerProxy?: {
        mimetype?: unknown;
        base64?: unknown;
    } | null;
}

interface ProcessingModeLike {
    kind?: unknown;
}

interface VideoStoragePolicyLike {
    persistOriginalInIndexedDb?: unknown;
}

export const VIDEO_ORIGINAL_STORAGE = Object.freeze({
    LOCAL_SYNC_REFERENCE: 'local-sync-reference',
    LEGACY_INDEXED_DB: 'legacy-indexeddb',
});

export const VIDEO_CLOUD_PLAYBACK_KIND = Object.freeze({
    COMPRESSED_PROXY: 'compressed-proxy',
    LEGACY_RAW_ORIGINAL: 'legacy-raw-original',
});

export interface PlayableCloudVideoSource {
    kind: typeof VIDEO_CLOUD_PLAYBACK_KIND[keyof typeof VIDEO_CLOUD_PLAYBACK_KIND];
    s3Key: string;
}

export interface PlayableWorkerVideoProxy {
    mimeType: string;
    base64: string;
}

export const VIDEO_ORIGINAL_CLOUD_UPLOAD_BLOCKED_REASON = 'raw_original_cloud_upload_forbidden';
export const VIDEO_REFERENCE_ONLY_DURABILITY_STATUS = 'local-reference-reconnect-required';
export const VIDEO_REFERENCE_ONLY_DURABILITY_LIMITATION = 'This video is not copied into browser or cloud storage. Keep the source folder available and reconnect it after reload to play or process the video.';

export function sanitizeVideoFileName(name: unknown, fallback = 'Video.mp4') {
    return String(name || fallback)
        .replace(/[<>:"/\\|?*\x00-\x1f]/g, '_')
        .replace(/\s+/g, '_')
        .replace(/^_+|_+$/g, '')
        || fallback;
}

export function getVideoSourcePath(fileOrVideo: VideoLike | null | undefined) {
    return String(
        fileOrVideo?.quoteMakerSourcePath
        || fileOrVideo?.relativePath
        || fileOrVideo?.webkitRelativePath
        || fileOrVideo?.localSourcePath
        || fileOrVideo?.syncFileName
        || fileOrVideo?.name
        || fileOrVideo?.originalName
        || ''
    );
}

/**
 * Return only cloud objects that VideoPreviewPlayer is permitted to request.
 * Current records must identify a compressed proxy explicitly. Raw originals
 * remain playable only for the shipped version-one opt-in or an explicitly
 * migrated legacy record, and never when a version-two policy is present.
 */
export function getPlayableCloudVideoSource(
    video: VideoLike | null | undefined,
): PlayableCloudVideoSource | null {
    const cloudKind = video?.cloudPlayback?.kind;
    const cloudPlaybackKey = typeof video?.cloudPlayback?.s3Key === 'string'
        ? video.cloudPlayback.s3Key.trim()
        : '';

    if (cloudKind === VIDEO_CLOUD_PLAYBACK_KIND.COMPRESSED_PROXY && cloudPlaybackKey) {
        return {
            kind: VIDEO_CLOUD_PLAYBACK_KIND.COMPRESSED_PROXY,
            s3Key: cloudPlaybackKey,
        };
    }

    const policyVersion = Number(video?.storagePolicy?.version);
    const isCurrentCompressedOnlyRecord = Number.isFinite(policyVersion) && policyVersion >= 2;
    const explicitlyMigratedLegacyRef = cloudKind === VIDEO_CLOUD_PLAYBACK_KIND.LEGACY_RAW_ORIGINAL;
    const shippedVersionOnePolicy = policyVersion === 1
        && video?.storagePolicy?.uploadOriginalToCloud === true;
    if (isCurrentCompressedOnlyRecord || (!explicitlyMigratedLegacyRef && !shippedVersionOnePolicy)) {
        return null;
    }

    const legacyKey = cloudPlaybackKey || (typeof video?.s3Key === 'string' ? video.s3Key.trim() : '');
    return legacyKey
        ? { kind: VIDEO_CLOUD_PLAYBACK_KIND.LEGACY_RAW_ORIGINAL, s3Key: legacyKey }
        : null;
}

/** Return an inline worker proxy only when the player can construct a video data URL. */
export function getPlayableWorkerVideoProxy(
    video: VideoLike | null | undefined,
): PlayableWorkerVideoProxy | null {
    const mimeType = typeof video?.workerProxy?.mimetype === 'string'
        ? video.workerProxy.mimetype.trim().toLowerCase()
        : '';
    const base64 = typeof video?.workerProxy?.base64 === 'string'
        ? video.workerProxy.base64.trim()
        : '';
    return mimeType.startsWith('video/') && base64
        ? { mimeType, base64 }
        : null;
}

export function buildVideoStoragePolicy(fileOrVideo: VideoLike | null | undefined, processingMode: ProcessingModeLike = {}) {
    const modeKind = processingMode?.kind || fileOrVideo?.processingMode || 'inline';
    const sourcePath = getVideoSourcePath(fileOrVideo);
    const syncFileName = sanitizeVideoFileName(fileOrVideo?.syncFileName || fileOrVideo?.name || fileOrVideo?.originalName);
    const persistOriginalInIndexedDb = modeKind === 'inline';
    const evidenceDerivatives = modeKind === 'inline'
        ? ['transcript', 'keyframes', 'poster_frame']
        : [];

    return {
        version: 2,
        canonicalSource: VIDEO_ORIGINAL_STORAGE.LOCAL_SYNC_REFERENCE,
        originalStorage: VIDEO_ORIGINAL_STORAGE.LOCAL_SYNC_REFERENCE,
        // Inline sources retain the existing safety-cache behaviour. A source
        // above the 512 MiB browser/cloud seam remains descriptor-only so an
        // accepted 2 GiB reference cannot fill IndexedDB or freeze the tab.
        persistOriginalInIndexedDb,
        uploadOriginalToCloud: false,
        originalCloudUpload: {
            status: 'blocked',
            reason: VIDEO_ORIGINAL_CLOUD_UPLOAD_BLOCKED_REASON,
        },
        evidenceDerivatives,
        processingMode: modeKind,
        syncFileName,
        sourcePath,
        localPlaybackRequired: true,
        indexedDbSafetyCache: persistOriginalInIndexedDb,
        durabilityStatus: persistOriginalInIndexedDb
            ? 'indexeddb-safety-cache-pending-sync'
            : VIDEO_REFERENCE_ONLY_DURABILITY_STATUS,
        durabilityLimitation: persistOriginalInIndexedDb
            ? null
            : VIDEO_REFERENCE_ONLY_DURABILITY_LIMITATION,
        note: persistOriginalInIndexedDb
            ? 'Raw video originals remain local. Only compressed video derivatives may be persisted to customer cloud storage.'
            : VIDEO_REFERENCE_ONLY_DURABILITY_LIMITATION,
        futureWorker: getLocalVideoWorkerStatus(),
    };
}

export function shouldPersistVideoOriginalInIndexedDb(policy: VideoStoragePolicyLike | null | undefined) {
    return policy?.persistOriginalInIndexedDb === true;
}

/**
 * Invoke a Blob-store operation only when the already-built policy permits it.
 * Passing the write as a callback makes suppression independently testable and
 * prevents a caller from eagerly materialising storage work for references.
 */
export async function persistVideoOriginalInIndexedDbIfAllowed<T>(
    policy: VideoStoragePolicyLike | null | undefined,
    persistOriginal: () => Promise<T>,
): Promise<T | null> {
    if (!shouldPersistVideoOriginalInIndexedDb(policy)) return null;
    return persistOriginal();
}

export function shouldUploadVideoOriginalToCloud(_policy: VideoStoragePolicyLike | null | undefined) {
    return false;
}

export function isLocalReferenceVideo(video: VideoLike | null | undefined) {
    return video?.storagePolicy?.originalStorage === VIDEO_ORIGINAL_STORAGE.LOCAL_SYNC_REFERENCE
        || video?.localSourceOnly === true
        || video?.canonicalSource === VIDEO_ORIGINAL_STORAGE.LOCAL_SYNC_REFERENCE;
}

/**
 * A saved reference descriptor is an intentional storage decision, not a lost
 * inline Blob. Check its complete policy, path and bounded size before intake
 * defers it. Do not recalculate the size tier: pre-512 MiB imports must retain
 * their truthful descriptor contract until their source is reconnected.
 */
export function isExplicitReferenceOnlyVideo(video: VideoLike | null | undefined): boolean {
    const policy = video?.storagePolicy;
    const size = Number(video?.size);
    const sourcePath = typeof policy?.sourcePath === 'string' ? policy.sourcePath.trim() : '';
    return video?.sourceMissing !== true
        && Number.isSafeInteger(size) && size > 0 && size <= getActiveHardCap()
        && video?.processingMode === 'reference_only'
        && video?.localSourceOnly === true
        && video?.canonicalSource === VIDEO_ORIGINAL_STORAGE.LOCAL_SYNC_REFERENCE
        && policy?.version === 2
        && policy?.originalStorage === VIDEO_ORIGINAL_STORAGE.LOCAL_SYNC_REFERENCE
        && policy?.canonicalSource === VIDEO_ORIGINAL_STORAGE.LOCAL_SYNC_REFERENCE
        && policy?.processingMode === 'reference_only'
        && policy?.persistOriginalInIndexedDb === false
        && policy?.uploadOriginalToCloud === false
        && policy?.durabilityStatus === VIDEO_REFERENCE_ONLY_DURABILITY_STATUS
        && sourcePath.length > 0
        && sourcePath === getVideoSourcePath(video).trim();
}
